<script setup lang="ts">
import { getBookingOptionQuantities, getBookingOptionQuantity } from "@/utils/bookingOptionQuantities";

const props = defineProps<{
  options: any[];
  selectedOptions?: string[];
  optionQuantities?: Record<string, number>;
  disabled?: boolean;
  checkAvailability: (id: string, quantity?: number) => Promise<boolean>;
}>();
const emit = defineEmits<{
  (event: "update:selectedOptions", value: string[]): void;
  (event: "update:optionQuantities", value: Record<string, number>): void;
  (event: "change"): void;
}>();

const toggle = async (option: any, selected: boolean) => {
  if (selected && !await props.checkAvailability(option.id, 1)) return;
  const options = selected ? [...(props.selectedOptions ?? []), option.id] : (props.selectedOptions ?? []).filter(id => id !== option.id);
  emit("update:optionQuantities", getBookingOptionQuantities(options, { ...props.optionQuantities, [option.id]: 1 }));
  emit("update:selectedOptions", options);
  emit("change");
};

const updateQuantity = async (id: string, value: unknown) => {
  const quantity = Number(value);
  emit("update:optionQuantities", { ...props.optionQuantities, [id]: quantity });
  if (Number.isSafeInteger(quantity) && quantity > 0) await props.checkAvailability(id, quantity);
  emit("change");
};

const quantityRule = (value: unknown) => Number.isSafeInteger(Number(value)) && Number(value) > 0 || "Enter a positive whole number";
</script>

<template>
  <div class="booking-extras">
    <div v-for="option in options" :key="option.id" class="booking-extra-row">
      <VCheckbox
        :value="option.id"
        :true-value="true"
        :false-value="false"
        :model-value="selectedOptions?.includes(option.id) ?? false"
        :label="`${option.name} ($${Number(option.sellPrice).toFixed(2)})`"
        :disabled="disabled"
        color="primary"
        density="compact"
        hide-details
        class="booking-extra-checkbox"
        @update:model-value="toggle(option, Boolean($event))"
      >
        <template #label>
          <span class="booking-extra-label">{{ `${option.name} ($${Number(option.sellPrice).toFixed(2)})` }}</span>
        </template>
      </VCheckbox>
      <VTextField
        v-if="option.allowBookingQuantity && selectedOptions?.includes(option.id)"
        :model-value="getBookingOptionQuantity(optionQuantities, option.id)"
        :disabled="disabled"
        :aria-label="`${option.name} quantity`"
        label="Qty"
        placeholder="Qty"
        type="number"
        min="1"
        step="1"
        :rules="[quantityRule]"
        density="compact"
        hide-details="auto"
        class="booking-extra-quantity"
        @update:model-value="updateQuantity(option.id, $event)"
      />
    </div>
  </div>
</template>

<style scoped>
.booking-extras {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 16px;
}

.booking-extra-row {
  display: flex;
  flex: 0 1 auto;
  max-width: 100%;
  align-items: start;
  gap: 8px;
}

.booking-extra-checkbox {
  flex: 0 1 auto;
  min-width: 0;
}

.booking-extra-label {
  white-space: normal;
}

.booking-extra-quantity {
  flex: 0 0 80px;
  width: 80px;
}
</style>